import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import ReactSelect from '../../../../components/ReactSelect';
import { GRID_SELECT } from '../../GscTools/selectStyles';
import {
    POLICIES,
    POLICY,
    addTopicError,
    caseInsensitive,
    definitionError,
    editorFromTopic,
    emptyTopic,
    filterLabel,
    policyLabel,
    topicFilters,
    topicFromEditor,
    topicMatch,
} from '../topics';

/**
 * The session's topics, written in the scope bar beside the dates they will be read over.
 *
 * A topic is a name, a match policy and what it matches on: literal terms separated by
 * semicolons, or one RE2 expression. Adding a topic under a name already in use replaces that
 * topic, and the pencil on a chip puts a topic back in the fields, so one form both writes and
 * edits a topic.
 *
 * The filters a topic will be sent as are previewed while it is typed, before anything is added
 * or applied — with its case rule beside them, because "search console" matches differently under
 * each policy and the difference is invisible in the terms alone.
 *
 * Nothing is saved. The topics live in this page's address once applied and nowhere else, and an
 * edit that has not been applied can be thrown away, which puts the applied topics back.
 */
class TopicEditor extends Component {
    constructor(props) {
        super(props);
        this.state = { draft: emptyTopic(), error: null };

        this.handleAdd = this.handleAdd.bind(this);
    }

    patch(partial) {
        this.setState((state) => ({ draft: { ...state.draft, ...partial }, error: null }));
    }

    handleAdd(event) {
        event.preventDefault();

        const { t, topics, onChange } = this.props;
        const topic = topicFromEditor(this.state.draft);

        // Invalid input keeps the topics in force as they were: nothing is added until the topic
        // is one Search Console can be sent.
        const error = addTopicError(topic, topics, t);
        if (error) return this.setState({ error });

        const exists = topics.some((item) => item.name === topic.name);
        onChange(
            exists
                ? topics.map((item) => (item.name === topic.name ? topic : item))
                : [...topics, topic],
        );
        this.setState({ draft: emptyTopic(), error: null });
    }

    handleEdit(topic) {
        this.setState({ draft: editorFromTopic(topic), error: null });
    }

    /** Whether the topics being edited differ from the ones the report on screen was read under. */
    edited() {
        const { topics, appliedTopics } = this.props;
        return !!appliedTopics && JSON.stringify(topics) !== JSON.stringify(appliedTopics);
    }

    renderPreview() {
        const { t } = this.props;
        const topic = topicFromEditor(this.state.draft);

        // A preview is only shown for a definition that could be sent; the name is not part of
        // what Search Console is told, so a topic still waiting for one is previewed anyway.
        const sendable = !definitionError(topic, t);

        return (
            <div className="brand-pattern-preview">
                <h5>{t('tools.topic_clusters.editor.preview')}</h5>
                {sendable ? (
                    <>
                        {topicFilters(topic).map((filter, index) => (
                            <code className="gsc-tool-expression" key={index}>
                                {filterLabel(filter)}
                            </code>
                        ))}
                        <p className="gsc-tool-rules-note">
                            {caseInsensitive(topic)
                                ? t('tools.topic_clusters.definition.case_insensitive')
                                : t('tools.topic_clusters.definition.case_sensitive')}
                            {' · '}
                            {t(`tools.topic_clusters.editor.policy_note.${topic.policy}`)}
                        </p>
                    </>
                ) : (
                    <span className="gsc-tool-field-note">
                        {t('tools.topic_clusters.editor.preview_empty')}
                    </span>
                )}
            </div>
        );
    }

    render() {
        const { t, topics, onChange, onRestore } = this.props;
        const { draft, error } = this.state;

        const policyOptions = POLICIES.map((value) => ({ value, label: policyLabel(t, value) }));
        const isRegex = draft.policy === POLICY.REGEX;
        const replacing = topics.some((item) => item.name === draft.name.trim());

        return (
            <div className="gsc-tool-rules">
                <div className="gsc-tool-rules-head">
                    <h4>{t('tools.topic_clusters.editor.title')}</h4>
                    <p>{t('tools.topic_clusters.editor.description')}</p>
                </div>

                <div className="gsc-tool-rules-grid wide-value-grid">
                    <div className="gsc-tool-field">
                        <label htmlFor="tc-topic-name">
                            {t('tools.topic_clusters.editor.name')}
                        </label>
                        <input
                            id="tc-topic-name"
                            type="text"
                            className="form-control"
                            value={draft.name}
                            placeholder={t('tools.topic_clusters.editor.name_placeholder')}
                            onChange={(event) => this.patch({ name: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label>{t('tools.topic_clusters.editor.policy')}</label>
                        <ReactSelect
                            options={policyOptions}
                            value={policyOptions.find((item) => item.value === draft.policy)}
                            blurInputOnSelect={true}
                            classname="gsc-tool-select"
                            controlStyles={GRID_SELECT}
                            customeOnChange={(item) => this.patch({ policy: item.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label htmlFor="tc-topic-match">
                            {isRegex
                                ? t('tools.topic_clusters.editor.pattern')
                                : t('tools.topic_clusters.editor.terms')}
                        </label>
                        <input
                            id="tc-topic-match"
                            type="text"
                            className="form-control"
                            value={draft.text}
                            placeholder={t(
                                `tools.topic_clusters.editor.placeholder.${draft.policy}`,
                            )}
                            onChange={(event) => this.patch({ text: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-rules-add">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleAdd}
                        >
                            {replacing
                                ? t('tools.topic_clusters.editor.update')
                                : t('tools.topic_clusters.editor.add')}
                        </button>
                    </div>
                </div>

                <p className="gsc-tool-rules-note">
                    {isRegex
                        ? t('tools.topic_clusters.editor.regex_note')
                        : t('tools.topic_clusters.editor.terms_note')}
                </p>

                {!!error && <div className="gsc-tool-banner error">{error}</div>}

                {this.renderPreview()}

                {!topics.length ? (
                    <p className="gsc-tool-field-note">{t('tools.topic_clusters.editor.none')}</p>
                ) : (
                    <div className="gsc-tool-chips">
                        {topics.map((topic) => {
                            const label = `${topic.name}: ${policyLabel(
                                t,
                                topic.policy,
                            )} · ${topicMatch(topic)}`;

                            return (
                                <span className="gsc-tool-chip" key={topic.name} title={label}>
                                    <span>{label}</span>
                                    <button
                                        type="button"
                                        aria-label={t('tools.topic_clusters.editor.edit', {
                                            topic: topic.name,
                                        })}
                                        onClick={() => this.handleEdit(topic)}
                                    >
                                        <i className="icon-pencil" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={t('tools.topic_clusters.editor.remove', {
                                            topic: topic.name,
                                        })}
                                        onClick={() =>
                                            onChange(
                                                topics.filter((item) => item.name !== topic.name),
                                            )
                                        }
                                    >
                                        <i className="icon-x-close" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                {this.edited() && (
                    <p className="gsc-tool-rules-note">
                        {t('tools.topic_clusters.editor.unapplied')}{' '}
                        <button type="button" className="button button-link" onClick={onRestore}>
                            {t('tools.topic_clusters.editor.restore')}
                        </button>
                    </p>
                )}
            </div>
        );
    }
}

export default withTranslation()(TopicEditor);
